import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

interface SdohReferralDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  memberId: string;
  needLabel: string;
  domain: string;
  alertId?: string;
}

const RESOURCE_TYPES = [
  { value: "community_organization", label: "Community Organization" },
  { value: "government_program", label: "Government Program" },
  { value: "food_bank", label: "Food Bank / Pantry" },
  { value: "housing_assistance", label: "Housing Assistance" },
  { value: "transportation_service", label: "Transportation Service" },
  { value: "mental_health", label: "Mental Health Service" },
  { value: "legal_aid", label: "Legal Aid" },
  { value: "other", label: "Other" },
];

export function SdohReferralDialog({
  open,
  onOpenChange,
  memberId,
  needLabel,
  domain,
  alertId,
}: SdohReferralDialogProps) {
  const queryClient = useQueryClient();
  const [resourceName, setResourceName] = useState("");
  const [resourceType, setResourceType] = useState("community_organization");
  const [resourceContact, setResourceContact] = useState("");
  const [notes, setNotes] = useState("");

  const createReferral = useMutation({
    mutationFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { error } = await supabase.from("sdoh_referrals" as any).insert({
        member_id: memberId,
        provider_id: user.id,
        alert_id: alertId || null,
        sdoh_domain: domain,
        need_label: needLabel,
        resource_name: resourceName,
        resource_type: resourceType,
        resource_contact: resourceContact || null,
        notes: notes || null,
        status: "referred",
      } as any);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Referral logged", { description: `${needLabel} → ${resourceName}` });
      queryClient.invalidateQueries({ queryKey: ["sdoh-referrals", memberId] });
      onOpenChange(false);
      resetForm();
    },
    onError: (err: any) => {
      toast.error("Failed to log referral", { description: err.message });
    },
  });

  const resetForm = () => {
    setResourceName("");
    setResourceType("community_organization");
    setResourceContact("");
    setNotes("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Log Referral — {needLabel}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="resource-name">Resource / Organization *</Label>
            <Input
              id="resource-name"
              placeholder="e.g., United Way, County Housing Authority"
              value={resourceName}
              onChange={(e) => setResourceName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="resource-type">Resource Type</Label>
            <Select value={resourceType} onValueChange={setResourceType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="resource-contact">Contact Info</Label>
            <Input
              id="resource-contact"
              placeholder="Phone, email, or website"
              value={resourceContact}
              onChange={(e) => setResourceContact(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="referral-notes">Notes</Label>
            <Textarea
              id="referral-notes"
              placeholder="Additional details about the referral..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => createReferral.mutate()}
            disabled={!resourceName.trim() || createReferral.isPending}
          >
            {createReferral.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Log Referral
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
